import React, { useEffect, useState } from "react";
import { Modal, Typography } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import { rtdb } from "../firebase";
import { ref, onValue } from "firebase/database";

const { Text } = Typography;

const STORAGE_KEY = "bioconnect_meteo_read";

/**
 * Retourne un objet avec titre et message détaillé pour la modale
 */
function getAlertDetails(weatherData) {
  const windSpeeds = weatherData.hourly?.wind_speed_10m || [];
  const temperatures = weatherData.hourly?.temperature_2m || [];
  const precipProb = weatherData.hourly?.precipitation_probability || [];
  const uvIndex = weatherData.hourly?.uv_index || [];

  const minTemp = Math.min(...temperatures);
  const maxTemp = Math.max(...temperatures);
  const maxWind = Math.max(...windSpeeds);
  const maxPrecip = Math.max(...precipProb);
  const maxUv = Math.max(...uvIndex);

  const hasFreeze = temperatures.some((temp) => temp < -2);
  const hasStrongWind = windSpeeds.some((speed) => speed > 35);
  const hasHighTemp = temperatures.some((temp) => temp > 28);

  // Construire le résumé des prévisions
  const forecastLines = [
    `🌡️ Températures : ${minTemp.toFixed(1)}°C à ${maxTemp.toFixed(1)}°C`,
    `💨 Vent max : ${maxWind.toFixed(0)} km/h`,
  ];
  if (maxPrecip > 0) {
    forecastLines.push(`🌧️ Risque de pluie max : ${maxPrecip.toFixed(0)}%`);
  }
  if (maxUv > 0) {
    forecastLines.push(`☀️ Indice UV max : ${maxUv.toFixed(1)}`);
  }

  const forecastSummary = forecastLines.join("\n");

  // Fonction utilitaire pour construire le message en HTML
  function buildMessage(description, risques, recommandations, forecast) {
    return {
      __html: true,
      description,
      risques,
      recommandations,
      forecast,
    };
  }

  if (hasFreeze && hasStrongWind) {
    return {
      title: "❄️💨 Alerte Gel et Vents Forts",
      message: buildMessage(
        `Les prévisions annoncent des températures négatives (min ${minTemp.toFixed(1)}°C) accompagnées de vents violents (${maxWind.toFixed(0)} km/h).`,
        [
          "Les arrivées d'eau exposées au gel pourraient éclater",
          "Les vents forts peuvent endommager les plants et la structure",
        ],
        [
          "Isolez vos canalisations et arrivées d'eau",
          "Débranchez et videz vos tuyaux souples",
          "Baissez les aérations de la serre",
        ],
        forecastSummary,
      ),
    };
  }
  if (hasFreeze) {
    return {
      title: "❄️ Alerte Gel",
      message: buildMessage(
        `Les prévisions annoncent des températures négatives (min ${minTemp.toFixed(1)}°C).`,
        [
          "Les arrivées d'eau exposées au gel pourraient éclater",
          "Les plants sensibles au gel peuvent être endommagés",
        ],
        [
          "Isolez vos canalisations et arrivées d'eau",
          "Débranchez et videz vos tuyaux souples",
          "Baissez les aérations de la serre",
        ],
        forecastSummary,
      ),
    };
  }
  if (hasStrongWind) {
    return {
      title: "💨 Alerte Vents Forts",
      message: buildMessage(
        `Les prévisions annoncent des vents violents (${maxWind.toFixed(0)} km/h).`,
        [
          "Les aérations ouvertes peuvent être arrachées",
          "La structure de la serre peut subir des contraintes",
        ],
        [
          "Baissez et sécurisez les aérations de la serre",
          "Rangez le matériel qui pourrait s'envoler",
        ],
        forecastSummary,
      ),
    };
  }
  if (hasHighTemp) {
    return {
      title: "🌡️ Alerte Températures Élevées",
      message: buildMessage(
        `Les prévisions annoncent des températures élevées (max ${maxTemp.toFixed(1)}°C).`,
        [
          "Surchauffe à l'intérieur de la serre",
          "Stress hydrique pour les plants",
        ],
        [
          "Ouvrez les aérations et portes pour ventiler",
          "Mettez en place un ombrage si possible",
        ],
        forecastSummary,
      ),
    };
  }
  return null;
}


/**
 * Récupère la liste des IDs déjà lus depuis le localStorage
 * Les IDs sont préfixés par l'ID de la serre pour dissociation
 */
function getReadIds(greenhouseId) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const all = raw ? JSON.parse(raw) : {};
    return all[greenhouseId] || [];
  } catch {
    return [];
  }
}

/**
 * Marque un ID comme lu dans le localStorage (préfixé par serre)
 */
function markAsRead(greenhouseId, dateKey) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const all = raw ? JSON.parse(raw) : {};
    const ids = all[greenhouseId] || [];
    if (!ids.includes(dateKey)) {
      ids.push(dateKey);
      all[greenhouseId] = ids;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
    }
  } catch {
    // ignore
  }
}

const WeatherAlertModal = ({ greenhouseId }) => {
  const [modalData, setModalData] = useState(null); // { dateKey, message }
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!greenhouseId) return;

    const meteoRef = ref(rtdb, `greenhouses/${greenhouseId}/meteo`);

    const unsubscribe = onValue(meteoRef, (snapshot) => {
      if (!snapshot.exists()) return;

      const meteoData = snapshot.val();
      const dateKeys = Object.keys(meteoData);
      if (dateKeys.length === 0) return;

      // Prendre la dernière entrée (la plus récente)
      const latestDateKey = dateKeys.sort().pop();
      const weatherData = meteoData[latestDateKey];

      // Vérifier si déjà lu (par serre)
      const readIds = getReadIds(greenhouseId);
      if (readIds.includes(latestDateKey)) return;

      // Déterminer les détails de l'alerte
      const details = getAlertDetails(weatherData);
      if (!details) return;

      // Afficher la modale
      setModalData({ dateKey: latestDateKey, title: details.title, message: details.message });
      setVisible(true);


    });

    return () => unsubscribe();
  }, [greenhouseId]);

  const handleClose = () => {
    if (modalData && greenhouseId) {
      markAsRead(greenhouseId, modalData.dateKey);
    }
    setVisible(false);
    setModalData(null);
  };

  // Rendu du message en HTML structuré
  const renderMessage = (msg) => {
    if (!msg || !msg.__html) {
      return <Text style={{ fontSize: 15 }}>{msg}</Text>;
    }
    return (
      <div className="weather-alert-text">
        <p>{msg.description}</p>
        <div>
          <strong>⚠️ Risques :</strong>
          <ul style={{ margin: "4px 0 12px 0", paddingLeft: 20 }}>
            {msg.risques.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>
        <div>
          <strong>✅ Recommandations :</strong>
          <ul style={{ margin: "4px 0 12px 0", paddingLeft: 20 }}>
            {msg.recommandations.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>
        <hr className="weather-alert-divider" />
        <div className="weather-alert-forecast">
          {msg.forecast}
        </div>
      </div>
    );
  };

  return (
    <Modal
      title={
        <span>
          <WarningOutlined style={{ color: "#faad14", marginRight: 8 }} />
          {modalData?.title || "🌤️ Alerte Météo"}
        </span>
      }
      open={visible}
      onOk={handleClose}
      onCancel={handleClose}
      okText="J'ai compris"
      cancelText="Fermer"
      centered
      width={480}
    >
      <div className="weather-alert-body">
        {renderMessage(modalData?.message)}
      </div>
    </Modal>
  );

};

export default WeatherAlertModal;
